import { useCallback, useEffect, useRef, useState } from "react";
import { getEvents, registerQueue, RegisterResult } from "./chatApi";
import { dmKeyFromUserIds } from "./useConversations";
import type { ZulipEvent, ZulipMessage } from "./types";

export type UnreadState = {
  count: number;
  // Unread messages that mention the caller (direct or wildcard) — a subset
  // of `count`, not additional on top of it. Drives the Mentions view's own
  // "new message" indicator (see ChatViewPane).
  mentions: number;
  // "streamId:topic" -> unread count, for the channel/topic list badges
  byChannelTopic: Record<string, number>;
  // dm participant key (sorted, self excluded — see dmKeyFromUserIds) -> unread count
  byDmKey: Record<string, number>;
};

const EMPTY: UnreadState = { count: 0, mentions: 0, byChannelTopic: {}, byDmKey: {} };

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function unreadFromRegister(reg: RegisterResult, myUserId: number): UnreadState {
  const byChannelTopic: Record<string, number> = {};
  for (const s of reg.unread_msgs.streams ?? []) {
    byChannelTopic[`${s.stream_id}:${s.topic}`] = s.unread_message_ids.length;
  }
  // Keys are built from the *other* participants only (self excluded),
  // matching how Selection.userIds and dmConversation.key are built — see
  // dmKeyFromUserIds's doc comment. `pms` entries are always the other
  // party's id already; `huddles.user_ids_string` is filtered defensively
  // in case it includes self on this Zulip version.
  const byDmKey: Record<string, number> = {};
  for (const pm of reg.unread_msgs.pms ?? []) {
    const key = dmKeyFromUserIds([pm.sender_id]);
    byDmKey[key] = (byDmKey[key] ?? 0) + pm.unread_message_ids.length;
  }
  for (const huddle of reg.unread_msgs.huddles ?? []) {
    const allIds = huddle.user_ids_string.split(",").map((s) => parseInt(s, 10));
    const otherIds = allIds.filter((id) => id !== myUserId);
    const key = dmKeyFromUserIds(otherIds.length ? otherIds : [myUserId]);
    byDmKey[key] = (byDmKey[key] ?? 0) + huddle.unread_message_ids.length;
  }
  return { count: reg.unread_msgs.count, mentions: reg.unread_msgs.mentions?.length ?? 0, byChannelTopic, byDmKey };
}

export type ZulipEventHandlers = {
  onMessage: (msg: ZulipMessage) => void;
  onSubscriptionChange: () => void;
  onTyping: (ev: ZulipEvent) => void;
  // A thumbnail backfill or content edit landed for one or more already-
  // loaded messages — patch their rendered HTML in place.
  onMessageUpdate: (messageIds: number[], renderedContent: string) => void;
  // A reaction was added or removed (by any user, including ourselves) —
  // patch the affected message's `reactions` array in place.
  onReaction: (ev: ZulipEvent) => void;
};

export type TypingIntervals = { startMs: number; stopMs: number };
// Zulip's own classic webapp defaults — used if an older server omits the
// server_typing_*_wait_period_milliseconds fields from `register`.
const DEFAULT_TYPING_INTERVALS: TypingIntervals = { startMs: 10000, stopMs: 5000 };

/**
 * Zulip's real long-polling real-time protocol (register + GET events),
 * replacing the earlier short-interval-polling approach: one queue is
 * opened via `register`, then `events` is called repeatedly — each call
 * blocks server-side until something happens (or a heartbeat, every few
 * minutes) — so there's no fixed-interval timer at all, just an
 * always-in-flight request. Runs for the whole session (not gated on the
 * panel being open) so the unread badge and DM list stay live in the
 * background, the same way Zulip's own clients behave.
 */
export function useZulipEvents(myUserId: number | null, ready: boolean, handlers: ZulipEventHandlers) {
  const [unread, setUnread] = useState<UnreadState>(EMPTY);
  const [typingIntervals, setTypingIntervals] = useState<TypingIntervals>(DEFAULT_TYPING_INTERVALS);
  const [enableReadReceipts, setEnableReadReceipts] = useState(false);
  // See RegisterResult.server_emoji_data_url — surfaced so ChatContext can
  // fetch Zulip's own canonical unicode-emoji names (emojiCodeMap.ts).
  const [emojiDataUrl, setEmojiDataUrl] = useState<string | null>(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const bumpUnread = useCallback((bucket: "byChannelTopic" | "byDmKey", key: string, delta: number) => {
    setUnread((prev) => {
      const nextBucket = { ...prev[bucket] };
      const nextVal = Math.max(0, (nextBucket[key] ?? 0) + delta);
      if (nextVal === 0) {
        delete nextBucket[key];
      } else {
        nextBucket[key] = nextVal;
      }
      return { ...prev, [bucket]: nextBucket, count: Math.max(0, prev.count + delta) };
    });
  }, []);

  const bumpMentions = useCallback((delta: number) => {
    setUnread((prev) => ({ ...prev, mentions: Math.max(0, prev.mentions + delta) }));
  }, []);

  useEffect(() => {
    if (!ready || !myUserId) return;
    const uid = myUserId; // narrowed to `number` here; captured so nested closures below keep that narrowing
    let stopped = false;

    function handleEvent(ev: ZulipEvent) {
      if (ev.type === "message" && ev.message) {
        // Zulip delivers this recipient's flags (mentioned, wildcard_mentioned,
        // etc.) as a sibling of `message` on the event, not nested inside it —
        // merge them in so isMention() in ChatContext can see them.
        handlersRef.current.onMessage({ ...ev.message, flags: ev.flags });
      } else if (ev.type === "update_message_flags") {
        if (ev.flag === "read" && ev.op === "add") {
          // Someone else's read-state change (e.g. another open session) —
          // rare enough that a full resync is simplest and cheap.
          registerQueue()
            .then((reg) => setUnread(unreadFromRegister(reg, uid)))
            .catch(() => {});
        }
      } else if (ev.type === "subscription") {
        handlersRef.current.onSubscriptionChange();
      } else if (ev.type === "typing") {
        handlersRef.current.onTyping(ev);
      } else if (ev.type === "update_message") {
        if (ev.rendered_content !== undefined && ev.message_id !== undefined) {
          handlersRef.current.onMessageUpdate([ev.message_id], ev.rendered_content);
        }
      } else if (ev.type === "reaction") {
        handlersRef.current.onReaction(ev);
      }
      // "heartbeat" and anything else: no local state to update.
    }

    function applyIntervals(reg: RegisterResult) {
      if (reg.server_typing_started_wait_period_milliseconds || reg.server_typing_stopped_wait_period_milliseconds) {
        setTypingIntervals({
          startMs: reg.server_typing_started_wait_period_milliseconds ?? DEFAULT_TYPING_INTERVALS.startMs,
          stopMs: reg.server_typing_stopped_wait_period_milliseconds ?? DEFAULT_TYPING_INTERVALS.stopMs,
        });
      }
    }

    async function loop() {
      let queueId: string;
      let lastEventId: number;
      let backoffMs = 1000;

      try {
        const reg = await registerQueue();
        queueId = reg.queue_id;
        lastEventId = reg.last_event_id;
        setUnread(unreadFromRegister(reg, uid));
        applyIntervals(reg);
        setEnableReadReceipts(!!reg.realm_enable_read_receipts);
        if (reg.server_emoji_data_url) setEmojiDataUrl(reg.server_emoji_data_url);
      } catch {
        return; // Chat unreachable at bootstrap — nothing to poll.
      }

      while (!stopped) {
        try {
          const res = await getEvents(queueId, lastEventId);
          if (stopped) break;
          for (const ev of res.events) {
            lastEventId = Math.max(lastEventId, ev.id);
            handleEvent(ev);
          }
          backoffMs = 1000;
        } catch (e: any) {
          if (stopped) break;
          // A generic network hiccup gets a short backoff before retrying;
          // an expired/invalid queue (BAD_EVENT_QUEUE_ID, or any other
          // rejection of the queue itself) goes straight to re-registering.
          if (e?.code !== "BAD_EVENT_QUEUE_ID") {
            await sleep(backoffMs);
            backoffMs = Math.min(backoffMs * 2, 30000);
          }
          try {
            const reg = await registerQueue();
            queueId = reg.queue_id;
            lastEventId = reg.last_event_id;
            setUnread(unreadFromRegister(reg, uid));
            applyIntervals(reg);
            setEnableReadReceipts(!!reg.realm_enable_read_receipts);
        if (reg.server_emoji_data_url) setEmojiDataUrl(reg.server_emoji_data_url);
            backoffMs = 1000;
          } catch {
            // stay in the loop — next iteration retries after the backoff above
          }
        }
      }
    }

    loop();
    return () => {
      stopped = true;
    };
  }, [ready, myUserId]);

  return { unread, bumpUnread, bumpMentions, typingIntervals, enableReadReceipts, emojiDataUrl };
}
